import { FormAutocomplete } from '@src/common/components/FormAutocomplete';
import { FormDropdown } from '@src/common/components/FormDropdown';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import { prefixFieldNameIfPrefixExists } from '@src/common/utils/formUtils';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { ControllerFormTextInput } from '@src/orders/PermitFormSections/ControllerFormTextInput';
import { useDisabledFields } from '@src/orders/PermitFormSections/useDisabledFields';
import {
  TrailerAxleCountField,
  TrailerMakeField,
  TrailerTypeField,
  TrailerUnitNumberField,
  TrailerVINField,
  TrailerYearField,
} from '@src/permitField/TrailerInformationFields';
import type { TrailerFormSchema } from '@src/trailer/TrailerFormSchema';
import type { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type * as yup from 'yup';

type form = yup.InferType<typeof TrailerFormSchema>;
type Props = {
  control: Control<yup.InferType<typeof TrailerFormSchema>>;
  readonly?: boolean;
  fieldsPrefix?: string;
  fields: string[];
};

export const TrailerInformationForm = ({
  control,
  readonly,
  fieldsPrefix,
  fields,
}: Props) => {
  const { t } = useTranslation();
  const { isFieldDisabled } = useDisabledFields();

  if (fields.length <= 0) {
    return null;
  }

  return (
    <Stack gap={theme.spacing['3']}>
      <Stack direction="row" gap={theme.spacing['3']}>
        <ConditionalField
          fields={fields}
          conditions={TrailerUnitNumberField.fieldName}
        >
          <ControllerFormTextInput
            control={control}
            disabled={
              readonly || isFieldDisabled(TrailerUnitNumberField.fieldName)
            }
            fieldName={prefixFieldNameIfPrefixExists<form>(
              TrailerUnitNumberField.fieldName,
              fieldsPrefix,
            )}
            label={t(`fields.${TrailerUnitNumberField.fieldName}`)}
          />
        </ConditionalField>

        <ConditionalField
          fields={fields}
          conditions={TrailerMakeField.fieldName}
        >
          <FormAutocomplete
            fieldName={prefixFieldNameIfPrefixExists<form>(
              TrailerMakeField.fieldName,
              fieldsPrefix,
            )}
            options={TrailerMakeField.options}
            control={control}
            label={t(`fields.${TrailerMakeField.fieldName}`)}
            disabled={isFieldDisabled(TrailerMakeField.fieldName)}
          />
        </ConditionalField>
      </Stack>

      <ConditionalField fields={fields} conditions={TrailerTypeField.fieldName}>
        <FormDropdown
          fieldName={prefixFieldNameIfPrefixExists<form>(
            TrailerTypeField.fieldName,
            fieldsPrefix,
          )}
          options={TrailerTypeField.options.map((option) => ({
            value: option,
            label: option,
          }))}
          control={control}
          label={t(`fields.${TrailerTypeField.fieldName}`)}
          disabled={isFieldDisabled(TrailerTypeField.fieldName)}
        />
      </ConditionalField>

      <Stack direction="row" gap={theme.spacing['3']}>
        <ConditionalField
          fields={fields}
          conditions={TrailerVINField.fieldName}
        >
          <ControllerFormTextInput
            uppercase
            control={control}
            fieldName={prefixFieldNameIfPrefixExists<form>(
              TrailerVINField.fieldName,
              fieldsPrefix,
            )}
            label={t(`fields.${TrailerVINField.fieldName}`)}
            disabled={isFieldDisabled(TrailerVINField.fieldName)}
          />
        </ConditionalField>
        <ConditionalField
          fields={fields}
          conditions={TrailerYearField.fieldName}
        >
          <ControllerFormTextInput
            control={control}
            fieldName={prefixFieldNameIfPrefixExists<form>(
              TrailerYearField.fieldName,
              fieldsPrefix,
            )}
            type="number"
            label={t(`fields.${TrailerYearField.fieldName}`)}
            disabled={isFieldDisabled(TrailerYearField.fieldName)}
          />
        </ConditionalField>

        <ConditionalField
          fields={fields}
          conditions={TrailerAxleCountField.fieldName}
        >
          <FormDropdown
            fieldName={prefixFieldNameIfPrefixExists<form>(
              TrailerAxleCountField.fieldName,
              fieldsPrefix,
            )}
            options={TrailerAxleCountField.options.map((option) => ({
              value: option,
              label: option,
            }))}
            control={control}
            label={t(`fields.${TrailerAxleCountField.fieldName}`)}
            disabled={isFieldDisabled(TrailerAxleCountField.fieldName)}
          />
        </ConditionalField>
      </Stack>
    </Stack>
  );
};
